import {
  pollForAccessToken,
  startPasswordlessLogin,
  TOKEN_POLL_TIMEOUT_MS,
} from '@paytrie/common/auth'
import { useCallback, useEffect, useRef, useState } from 'react'

import { logger } from '@/utils/logger'

type PasswordlessClient = Parameters<typeof startPasswordlessLogin>[0]['client']

type VerifiedPayload = {
  email: string
  accessToken: string
}

type SendArgs = {
  email: string
  referral?: string
  vendor?: string
}

export const usePasswordlessAuth = ({
  client,
  onVerified,
}: {
  client: PasswordlessClient
  onVerified: (payload: VerifiedPayload) => void
}) => {
  const [waitingForApproval, setWaitingForApproval] = useState(false)
  const [denied, setDenied] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const pollAbortRef = useRef<AbortController | null>(null)
  const activeSessionRef = useRef<{
    sessionId: string
    email: string
  } | null>(null)

  useEffect(() => () => pollAbortRef.current?.abort(), [])

  const beginPoll = useCallback(
    (session: { sessionId: string; email: string }) => {
      pollAbortRef.current?.abort()
      const controller = new AbortController()
      pollAbortRef.current = controller

      pollForAccessToken({
        sessionId: session.sessionId,
        signal: controller.signal,
      })
        .then((result) => {
          if (result.status === 'verified') {
            activeSessionRef.current = null
            onVerified({
              email: session.email,
              accessToken: result.accessToken,
            })
            return
          }
          if (result.status === 'denied') {
            activeSessionRef.current = null
            setWaitingForApproval(false)
            setLoading(false)
            setDenied(true)
            return
          }
          if (controller.signal.aborted) return
          if (!activeSessionRef.current) return
          activeSessionRef.current = null
          setErrorMessage(
            result.status === 'timed-out'
              ? 'The verification link expired. Please request a new one.'
              : 'Something went wrong. Please try again.'
          )
          setWaitingForApproval(false)
          setLoading(false)
        })
        .catch((err) => {
          if (controller.signal.aborted) return
          if (!activeSessionRef.current) return
          activeSessionRef.current = null
          logger.error('Magic link poll failed', err)
          setErrorMessage('Something went wrong. Please try again.')
          setWaitingForApproval(false)
          setLoading(false)
        })
    },
    [onVerified]
  )

  const beginPollRef = useRef(beginPoll)
  useEffect(() => {
    beginPollRef.current = beginPoll
  })

  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible' && activeSessionRef.current) {
        beginPollRef.current(activeSessionRef.current)
      }
    }
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [])

  const sendMagicLink = useCallback(
    async ({ email, referral, vendor }: SendArgs): Promise<boolean> => {
      setLoading(true)
      setErrorMessage('')
      setDenied(false)
      pollAbortRef.current?.abort()

      try {
        const normalizedEmail = email.toLowerCase().trim()
        const { sessionId } = await startPasswordlessLogin({
          email: normalizedEmail,
          client,
          referral,
          vendor,
        })

        try {
          window.clarity?.('set', 'paytrie_session_id', sessionId)
        } catch {
          //
        }

        const session = { sessionId, email: normalizedEmail }
        activeSessionRef.current = session
        setWaitingForApproval(true)
        beginPoll(session)
        return true
      } catch (err) {
        setErrorMessage(
          err instanceof Error
            ? err.message
            : 'Failed to send verification link. Please try again.'
        )
        logger.error('Magic link request failed', err)
        return false
      } finally {
        setLoading(false)
      }
    },
    [beginPoll, client]
  )

  const resetDenied = useCallback(() => {
    setDenied(false)
    setErrorMessage('')
  }, [])

  return {
    sendMagicLink,
    waitingForApproval,
    denied,
    loading,
    errorMessage,
    resetDenied,
    pollTimeoutMs: TOKEN_POLL_TIMEOUT_MS,
  }
}
